import {
  Alert,
  AlertDescription,
  Button,
  ButtonGroup,
  Checkbox,
  Popover,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { t } from "i18next";
import { FilterTag } from "./FilterTag";
import {
  LifeCycleStage,
  tLifeCycleStage,
} from "../../../../cra/models/LifeCycleStage";
import { useStores } from "../../../../shared/stores/RootStore";
import { CustomPopover } from "../../../../shared/views/CustomPopover";
import { AddFilterButton } from "./AddFilterButton";

const LifeCycleStageFiltersBase: React.FC = () => {
  const { filterStore, craForecastStore } = useStores();
  const { open, onToggle, onClose } = useDisclosure();
  const [selectedItems, setSelectedItems] = useState(new Set<LifeCycleStage>());
  const forecastMode = craForecastStore.isForecastModeActivated();

  useEffect(() => {
    if (open) {
      setSelectedItems(() => new Set(filterStore.lifeCycleStageFilters));
    }
    //eslint-disable-next-line
  }, [open]);

  const handleClear = () => {
    setSelectedItems(new Set());
  };

  const handleSubmit = () => {
    filterStore.setLifeCycleStageFilters([...selectedItems]);
    onClose();
  };

  const removeActiveFilter = (stage: LifeCycleStage) => {
    filterStore.setLifeCycleStageFilters(
      filterStore.lifeCycleStageFilters.filter((item) => item !== stage),
    );
  };

  return (
    <>
      <CustomPopover
        open={open}
        onClose={onClose}
        asChild={false}
        trigger={
          <AddFilterButton label="Life cycle stage" onClick={onToggle} />
        }
      >
        <Popover.Header>{t("map:filters.byStageTitle")}</Popover.Header>
        <Popover.Body maxH="360px" overflowY="auto">
          <VStack alignItems="start">
            {Object.values(LifeCycleStage).map((stage, i) => (
              <Checkbox.Root
                key={i}
                colorPalette="primary"
                name={stage}
                checked={selectedItems.has(stage)}
                disabled={
                  forecastMode &&
                  stage !== LifeCycleStage.Review &&
                  stage !== LifeCycleStage.Completed
                }
                onCheckedChange={({ checked }) => {
                  setSelectedItems((prev) => {
                    if (checked) {
                      prev.add(stage);
                    } else {
                      prev.delete(stage);
                    }
                    return new Set(prev);
                  });
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
                <Checkbox.Label>
                  <Text fontSize="sm" lineClamp={1}>
                    {tLifeCycleStage(stage)}
                  </Text>
                </Checkbox.Label>
              </Checkbox.Root>
            ))}
          </VStack>
        </Popover.Body>
        <Popover.Footer display="flex" justifyContent="flex-end">
          <ButtonGroup colorPalette="primary" size="sm">
            <Button onClick={handleClear} disabled={!selectedItems.size}>
              {t("map:filters.clearButton")}
            </Button>
            <Button onClick={handleSubmit}>
              {t("map:filters.submitButton")}
            </Button>
          </ButtonGroup>
        </Popover.Footer>
      </CustomPopover>

      {!!filterStore.lifeCycleStageFilters.length &&
        filterStore.lifeCycleStageFilters.map((stage, i) => (
          <FilterTag key={i} onClose={() => removeActiveFilter(stage)}>
            {tLifeCycleStage(stage)}
          </FilterTag>
        ))}

      {forecastMode && filterStore.lifeCycleStageFilters.length === 2 && (
        <Alert.Root status={"info"}>
          <Alert.Indicator />
          <AlertDescription fontSize={"xs"}>
            Review and Completed stages are selected by default in forecast
            scenario mode.
          </AlertDescription>
        </Alert.Root>
      )}
    </>
  );
};

export const LifeCycleStageFilters = observer(LifeCycleStageFiltersBase);
